import { Image, StyleSheet, Text } from 'react-native'
import { SquareCard } from '@/src/common/components/SquareCard'
import { Button } from '@/src/common/components/Button'
import { Link } from 'expo-router'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import type { ComponentProps } from 'react'
import { MesaMonogram } from '@/src/assets/icons/MesaMonogram'
import {
  useCardDesignParameterStore,
  CardImagesSchema,
} from '@/src/services/Statsig/useCardDesignParameterStore'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { Loading } from '@/src/common/components/Loading'
import { useMediaQuery } from 'react-responsive'
import { fontFamilies, mesaColorNames, typography } from '@/src/common/theme'
import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'
import * as Mortgage from '@/src/experiences/mortgage'
import { HomeE2E } from '@/src/experiences/home/e2e'

const width = 292
const aspectRatio = width / 188

export function MesaCardImage(props: ComponentProps<typeof Image>) {
  const cardDesignStore = useCardDesignParameterStore()
  const { loading: userDataLoading } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'cache-first',
  })
  const { sourcePartnerName } = useSourcePartnerName()

  const cardDesigns = CardImagesSchema.parse(cardDesignStore.get('card_details', {}))
  const partnerName = sourcePartnerName
  const remoteCardImageURL = cardDesigns[partnerName]?.image_url

  if (userDataLoading) {
    return <Loading size="small" />
  }

  return (
    <Image
      source={
        remoteCardImageURL
          ? { uri: remoteCardImageURL }
          : require('@/src/assets/images/Mesa_Card.png')
      }
      resizeMode="contain"
      {...props}
      style={[{ maxWidth: '88%', width, height: 'auto', aspectRatio, margin: 16 }, props.style]}
    />
  )
}

export function CardSection({ style }: Pick<ComponentProps<typeof SquareCard>, 'style'>) {
  const { sourcePartnerName } = useSourcePartnerName()
  const moduleDesign = Mortgage.Statsig.Store.useMortgageModuleDesign(sourcePartnerName)
  const { card_name } = moduleDesign
  const mesaCardName = card_name || 'Mesa Homeowners Card'
  const minWidth = useMediaQuery({ minWidth: 720 }) ? '42%' : '88%'
  return (
    <Link href="/main/card/cardBenefits" asChild push>
      <SquareCard
        style={StyleSheet.flatten([
          {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            borderRadius: 16,
            padding: 0,
            minWidth,
          },
          style,
        ])}
        segmentAction='User Pressed the "Card Benefits" card component'
        background={false}
        testID={HomeE2E.Ids.View.CardSection}
      >
        <MesaMonogram style={StyleSheet.absoluteFill} />
        <MesaCardImage />
        <Text
          style={[
            typography['heading/heading2'],
            { color: mesaColorNames['Greyscale/0 #FFFFFF'], textAlign: 'center' },
          ]}
        >
          {mesaCardName}
        </Text>
        <Text
          style={{
            fontFamily: fontFamilies['PlusJakartaSans-Regular'],
            fontWeight: 400,
            fontSize: 14,
            lineHeight: 18.9,
            letterSpacing: 0,
            color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
            marginHorizontal: 16,
            marginTop: 6,
            textAlign: 'center',
          }}
        >
          Earn Mesa Points on home-related purchases, even your monthly mortgage payments!
          {sourcePartnerName === 'lakeview' && ' Terms Apply.'}
        </Text>
        <Link href="/main/card/cardBenefits" asChild push>
          <Button
            segmentAction='User Pressed the "Card Benefits" card component'
            containerStyle={{
              marginTop: 12,
              marginBottom: 35,
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'stretch',
            }}
            buttonStyle={{ paddingVertical: 8, paddingHorizontal: 16, flex: 1 }}
            size="sm"
            title="Learn More"
          />
        </Link>
      </SquareCard>
    </Link>
  )
}
